import { useEffect, useState } from 'react';
import { api } from '../services/api';

interface Position {
  account: string;
  symbol: string;
  baseAsset: string;
  quantity: number;
  costBasis: number;
  currentPrice: number;
  positionValue: number;
  costValue: number;
  unrealizedPnl: number;
  unrealizedPnlPct: number;
  usdtBalance: number;
  totalValue: number;
  historicalBuyVolume: number;
  historicalSellVolume: number;
  netTradingPnl: number;
  error?: string;
}

interface PositionsResponse {
  positions: Position[];
  currentPrice: number;
  timestamp: string;
}

interface PositionTrackerProps {
  refreshInterval?: number;
}

export function PositionTracker({ refreshInterval = 30000 }: PositionTrackerProps) {
  const [data, setData] = useState<PositionsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadPositions();
    const interval = setInterval(loadPositions, refreshInterval);
    return () => clearInterval(interval);
  }, [refreshInterval]);

  const loadPositions = async () => {
    try {
      const response = await api.getPositions();
      setData(response);
      setLoading(false);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load positions');
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const formatPrice = (value: number) => `$${value.toFixed(5)}`;
  const formatQty = (value: number) => value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  const formatPct = (value: number) => `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;

  const getPnlClass = (value: number) => value >= 0 ? 'positive' : 'negative';

  const totalValue = data?.positions.reduce((sum, p) => sum + (p.error ? 0 : p.totalValue), 0) || 0;
  const totalUnrealizedPnl = data?.positions.reduce((sum, p) => sum + (p.error ? 0 : p.unrealizedPnl), 0) || 0;
  const totalFco = data?.positions.reduce((sum, p) => sum + (p.error ? 0 : p.quantity), 0) || 0;

  if (loading && !data) {
    return <div className="position-tracker loading">Loading positions...</div>;
  }

  if (error) {
    return <div className="position-tracker error">{error}</div>;
  }

  return (
    <div className="position-tracker">
      <div className="tracker-header">
        <h3>Position Tracker</h3>
        <div className="header-info">
          <span className="current-price">FCO: {formatPrice(data?.currentPrice || 0)}</span>
          <button onClick={loadPositions} className="refresh-btn" title="Refresh">↻</button>
        </div>
      </div>

      <div className="portfolio-summary">
        <div className="summary-card">
          <span className="label">Total Value</span>
          <span className="value">{formatCurrency(totalValue)}</span>
        </div>
        <div className="summary-card">
          <span className="label">Total FCO</span>
          <span className="value">{formatQty(totalFco)}</span>
        </div>
        <div className="summary-card">
          <span className="label">Unrealized P&L</span>
          <span className={`value ${getPnlClass(totalUnrealizedPnl)}`}>
            {formatCurrency(totalUnrealizedPnl)}
          </span>
        </div>
      </div>

      <div className="positions-table">
        <div className="table-header">
          <span>Account</span>
          <span>FCO Balance</span>
          <span>Cost Basis</span>
          <span>Current Price</span>
          <span>Position Value</span>
          <span>Unrealized P&L</span>
          <span>USDT</span>
          <span>Total Value</span>
        </div>

        {data?.positions.map((position) => (
          <div key={position.account} className={`position-row ${position.error ? 'error' : ''}`}>
            {position.error ? (
              <>
                <span className="account">{position.account}</span>
                <span className="error-msg" style={{ gridColumn: 'span 7' }}>{position.error}</span>
              </>
            ) : (
              <>
                <span className="account">{position.account}</span>
                <span className="quantity">{formatQty(position.quantity)}</span>
                <span className="cost-basis">{formatPrice(position.costBasis)}</span>
                <span className="current-price">{formatPrice(position.currentPrice)}</span>
                <span className="position-value">{formatCurrency(position.positionValue)}</span>
                <span className={`pnl ${getPnlClass(position.unrealizedPnl)}`}>
                  {formatCurrency(position.unrealizedPnl)} ({formatPct(position.unrealizedPnlPct)})
                </span>
                <span className="usdt">{formatCurrency(position.usdtBalance)}</span>
                <span className="total-value">{formatCurrency(position.totalValue)}</span>
              </>
            )}
          </div>
        ))}
      </div>

      <div className="historical-stats">
        <h4>Historical Trading</h4>
        <div className="stats-grid">
          {data?.positions.filter(p => !p.error).map((position) => (
            <div key={position.account} className="stat-card">
              <span className="account">{position.account}</span>
              <div className="stat-row">
                <span>Buy Volume:</span>
                <span>{formatCurrency(position.historicalBuyVolume)}</span>
              </div>
              <div className="stat-row">
                <span>Sell Volume:</span>
                <span>{formatCurrency(position.historicalSellVolume)}</span>
              </div>
              <div className={`stat-row ${getPnlClass(position.netTradingPnl)}`}>
                <span>Net P&L:</span>
                <span>{formatCurrency(position.netTradingPnl)}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {data?.timestamp && (
        <div className="last-update">
          Last updated: {new Date(data.timestamp).toLocaleTimeString()}
        </div>
      )}
    </div>
  );
}
